(function() {
  'use strict';

  if (!customElements.get('section-benefits-bar')) {
    customElements.define(
      'section-benefits-bar',
      class BenefitsBar extends HTMLElement {
        constructor() {
          super();
          this.marquee = null;
          this.track = null;
          this.animationId = null;
          this.isPaused = false;
          this.currentPosition = 0;
          this.halfWidth = 0;
          this.lastTimestamp = 0;
          this.isInitialized = false;
          this.boundHandlers = {};
        }

        connectedCallback() {
          // Defer initialization to ensure DOM is ready
          if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.init(), { once: true });
          } else {
            // Use requestAnimationFrame to ensure layout is complete
            requestAnimationFrame(() => this.init());
          }
        }

        disconnectedCallback() {
          this.cleanup();
        }

        init() {
          if (this.isInitialized) return;

          this.track = this.querySelector('.benefits-bar__track');
          this.marquee = this.querySelector('.benefits-bar__marquee');

          if (!this.track || !this.marquee) {
            return;
          }

          // Parse settings from data attributes
          this.animationSpeed = parseFloat(this.dataset.animationSpeed) || 3;
          this.enableDesktopAnimation = this.dataset.enableDesktopAnimation === 'true';
          this.mobileBreakpoint = 640;

          // Check for reduced motion preference
          this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
          this.reducedMotion = this.reducedMotionQuery.matches;

          // Bind all handlers once to maintain references for removal
          this.boundHandlers = {
            resize: this.debounce(() => this.handleResize(), 250),
            visibilityChange: () => this.handleVisibilityChange(),
            reducedMotionChange: (e) => this.handleReducedMotionChange(e),
            touchStart: (e) => this.handleTouchStart(e),
            touchMove: (e) => this.handleTouchMove(e),
            touchEnd: () => this.handleTouchEnd(),
            mouseEnter: () => this.handleMouseEnter(),
            mouseLeave: () => this.handleMouseLeave()
          };

          // Add reduced motion listener for dynamic changes
          this.reducedMotionQuery.addEventListener('change', this.boundHandlers.reducedMotionChange);

          // Check if we should animate
          this.isMobile = this.checkIsMobile();
          this.updateAnimationState();

          // Add resize listener
          window.addEventListener('resize', this.boundHandlers.resize);

          // Add visibility change listener for iOS tab switching
          document.addEventListener('visibilitychange', this.boundHandlers.visibilityChange);

          // Handle theme editor
          if (typeof Shopify !== 'undefined' && Shopify.designMode) {
            document.addEventListener('shopify:section:load', (event) => {
              if (event.target === this || this.contains(event.target)) {
                this.cleanup();
                this.isInitialized = false;
                this.init();
              }
            });
          }

          this.isInitialized = true;
        }

        checkIsMobile() {
          // Use both viewport width and touch capability for more reliable detection
          const viewportWidth = Math.min(window.innerWidth, document.documentElement.clientWidth);
          const isMobileViewport = viewportWidth <= this.mobileBreakpoint;

          // Check for touch capability as additional signal
          const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

          // On iOS, also check for standalone mode
          const isStandalone = window.navigator.standalone === true;

          return isMobileViewport || (hasTouch && viewportWidth <= this.mobileBreakpoint + 100);
        }

        updateAnimationState() {
          const shouldAnimate = (this.isMobile || this.enableDesktopAnimation) && !this.reducedMotion;

          // Update desktop animation class
          if (this.enableDesktopAnimation && !this.isMobile) {
            this.classList.add('benefits-bar--desktop-animation');
          } else {
            this.classList.remove('benefits-bar--desktop-animation');
          }

          if (shouldAnimate) {
            this.setupMarquee();
            this.setupTouchHandlers();
            this.startAnimation();
          } else {
            this.stopAnimation();
            this.removeTouchHandlers();
          }
        }

        handleResize() {
          const wasMobile = this.isMobile;
          this.isMobile = this.checkIsMobile();

          // Recalculate marquee width on resize
          if (this.isMobile || this.enableDesktopAnimation) {
            this.setupMarquee();
          }

          this.updateAnimationState();
        }

        handleVisibilityChange() {
          if (document.hidden) {
            // Tab is hidden, pause animation to save resources
            this.isPaused = true;
            this.stopAnimation();
          } else {
            // Tab is visible again, resume animation
            this.isPaused = false;
            const shouldAnimate = (this.isMobile || this.enableDesktopAnimation) && !this.reducedMotion;
            if (shouldAnimate) {
              this.lastTimestamp = 0; // Reset timestamp to prevent jump
              this.startAnimation();
            }
          }
        }

        handleReducedMotionChange(event) {
          this.reducedMotion = event.matches;
          this.updateAnimationState();
        }

        setupMarquee() {
          if (!this.track) return;

          // Calculate track width
          const items = this.track.querySelectorAll('.benefits-bar__item');
          if (items.length === 0) return;

          // Reset position
          this.currentPosition = 0;
          this.applyTransform(0);

          // Calculate total width of first set of items
          // Items are duplicated, so we only measure half
          let firstSetWidth = 0;
          const firstSetCount = Math.floor(items.length / 2);

          for (let i = 0; i < firstSetCount; i++) {
            const item = items[i];
            // Force layout calculation
            const rect = item.getBoundingClientRect();
            firstSetWidth += rect.width;
            if (i < firstSetCount - 1) {
              firstSetWidth += 40; // gap value from CSS
            }
          }

          // Store half width for seamless loop
          this.halfWidth = firstSetWidth + 40; // Add one gap for the loop
        }

        setupTouchHandlers() {
          if (!this.marquee) return;

          // Remove existing handlers first to prevent duplicates
          this.removeTouchHandlers();

          // Use passive: false for touchmove to allow preventDefault if needed
          this.marquee.addEventListener('touchstart', this.boundHandlers.touchStart, { passive: true });
          this.marquee.addEventListener('touchmove', this.boundHandlers.touchMove, { passive: true });
          this.marquee.addEventListener('touchend', this.boundHandlers.touchEnd, { passive: true });
          this.marquee.addEventListener('touchcancel', this.boundHandlers.touchEnd, { passive: true });
          this.marquee.addEventListener('mouseenter', this.boundHandlers.mouseEnter);
          this.marquee.addEventListener('mouseleave', this.boundHandlers.mouseLeave);
        }

        removeTouchHandlers() {
          if (!this.marquee) return;

          this.marquee.removeEventListener('touchstart', this.boundHandlers.touchStart);
          this.marquee.removeEventListener('touchmove', this.boundHandlers.touchMove);
          this.marquee.removeEventListener('touchend', this.boundHandlers.touchEnd);
          this.marquee.removeEventListener('touchcancel', this.boundHandlers.touchEnd);
          this.marquee.removeEventListener('mouseenter', this.boundHandlers.mouseEnter);
          this.marquee.removeEventListener('mouseleave', this.boundHandlers.mouseLeave);
        }

        handleTouchStart(e) {
          if (!e.touches || !e.touches[0]) return;

          this.touchStartX = e.touches[0].clientX;
          this.touchStartY = e.touches[0].clientY;
          this.isDragging = false;
          this.dragOffset = 0;
          this.isPaused = true;
        }

        handleTouchMove(e) {
          if (!this.isPaused || !e.touches || !e.touches[0]) return;

          const touchX = e.touches[0].clientX;
          const touchY = e.touches[0].clientY;
          const deltaX = Math.abs(touchX - this.touchStartX);
          const deltaY = Math.abs(touchY - this.touchStartY);

          // Determine if user is dragging horizontally
          if (deltaX > deltaY && deltaX > 10) {
            this.isDragging = true;
            this.dragOffset = touchX - this.touchStartX;
            this.applyTransform(this.currentPosition + this.dragOffset);
          }
        }

        handleTouchEnd() {
          if (this.isDragging) {
            // Update position based on drag
            this.currentPosition += this.dragOffset;
            this.dragOffset = 0;
            this.isDragging = false;

            // Normalize position for seamless infinite loop
            if (this.halfWidth > 0) {
              // If dragged too far right (positive), wrap to negative
              while (this.currentPosition > 0) {
                this.currentPosition -= this.halfWidth;
              }
              // If dragged too far left (very negative), wrap back
              while (this.currentPosition <= -this.halfWidth) {
                this.currentPosition += this.halfWidth;
              }
            }
          }

          // Resume animation after a short delay
          setTimeout(() => {
            this.isPaused = false;
            this.lastTimestamp = 0; // Reset timestamp to prevent jump
          }, 500);
        }

        handleMouseEnter() {
          this.isPaused = true;
        }

        handleMouseLeave() {
          this.isPaused = false;
          this.lastTimestamp = 0; // Reset timestamp to prevent jump
        }

        startAnimation() {
          if (this.reducedMotion || !this.track) {
            return;
          }

          // Check if animation should run (mobile OR desktop animation enabled)
          const shouldAnimate = this.isMobile || this.enableDesktopAnimation;
          if (!shouldAnimate) {
            return;
          }

          this.stopAnimation();
          this.lastTimestamp = 0;
          this.animationId = requestAnimationFrame((timestamp) => this.animate(timestamp));
        }

        stopAnimation() {
          if (this.animationId) {
            cancelAnimationFrame(this.animationId);
            this.animationId = null;
          }
        }

        animate(timestamp) {
          // Check if we should still be animating
          const shouldAnimate = (this.isMobile || this.enableDesktopAnimation) && !this.reducedMotion;

          if (!this.track || !shouldAnimate) {
            this.animationId = null;
            return;
          }

          // If paused, keep the animation loop running but don't move
          if (this.isPaused || document.hidden) {
            this.lastTimestamp = 0;
            this.animationId = requestAnimationFrame((ts) => this.animate(ts));
            return;
          }

          // Calculate delta time for consistent animation speed across devices
          if (this.lastTimestamp === 0) {
            this.lastTimestamp = timestamp;
          }

          const deltaTime = timestamp - this.lastTimestamp;
          this.lastTimestamp = timestamp;

          // Limit delta to prevent large jumps after tab switch
          const clampedDelta = Math.min(deltaTime, 100);

          // Calculate speed: higher number = faster
          // Speed setting 1-10, where 1 is slowest, 10 is fastest
          // Use time-based movement for consistent speed
          const baseSpeed = 0.05; // pixels per millisecond
          const speedMultiplier = this.animationSpeed / 3; // normalize to default of 3
          const moveAmount = baseSpeed * speedMultiplier * clampedDelta;

          this.currentPosition -= moveAmount;

          // Reset position seamlessly when we've scrolled one full set
          // When position reaches -halfWidth, the duplicate set is exactly where the original was
          // So we reset to 0 for a seamless infinite loop
          if (this.halfWidth > 0 && this.currentPosition <= -this.halfWidth) {
            this.currentPosition += this.halfWidth;
          }

          this.applyTransform(this.currentPosition);

          this.animationId = requestAnimationFrame((ts) => this.animate(ts));
        }

        applyTransform(position) {
          if (!this.track) return;

          // Use translate3d for hardware acceleration on iOS
          const transform = `translate3d(${position}px, 0, 0)`;
          this.track.style.transform = transform;
          this.track.style.webkitTransform = transform;
        }

        debounce(func, wait) {
          let timeout;
          return (...args) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(this, args), wait);
          };
        }

        cleanup() {
          this.stopAnimation();
          this.removeTouchHandlers();

          if (this.boundHandlers.resize) {
            window.removeEventListener('resize', this.boundHandlers.resize);
          }

          if (this.boundHandlers.visibilityChange) {
            document.removeEventListener('visibilitychange', this.boundHandlers.visibilityChange);
          }

          if (this.reducedMotionQuery && this.boundHandlers.reducedMotionChange) {
            this.reducedMotionQuery.removeEventListener('change', this.boundHandlers.reducedMotionChange);
          }
        }
      }
    );
  }
})();
